/**
 * Copyright (c) 2021-2025 Kognitos, Inc. All rights reserved.
 */
import React, { createContext, use, useMemo } from 'react';
import type { AppConfig } from './AppConfig';
import { getRuntimeConfig } from './runtimeConfig';

interface ConfigContextType {
  config: AppConfig;
}

// eslint-disable-next-line no-restricted-syntax -- reason: App initialization context required before stores are available. Configuration must be accessible at app bootstrap.
const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: React.ReactNode;
  config?: AppConfig; // Allow override for testing
}

/**
 * Provides application configuration to the React tree.
 *
 * Configuration sources:
 * - Props: For testing overrides
 * - window.__RUNTIME_CONFIG__: Injected by Vite (dev) or nginx (production)
 */
export function ConfigProvider({ children, config }: ConfigProviderProps) {
  // Use provided config (testing) or get from window.__RUNTIME_CONFIG__
  const runtimeConfig = getRuntimeConfig();

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    const appConfig: AppConfig = config ?? {
      auth0Domain: runtimeConfig.auth0Domain,
      auth0ClientId: runtimeConfig.auth0ClientId,
      auth0Audience: runtimeConfig.auth0Audience,
      auth0CustomAudience: runtimeConfig.auth0CustomAudience,
      sentryDsn: runtimeConfig.sentryDsn ?? '',
    };

    return { config: appConfig };
  }, [config, runtimeConfig]);

  return <ConfigContext value={contextValue}>{children}</ConfigContext>;
}

export function useConfig(): AppConfig {
  const context = use(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context.config;
}
